import { ImageSizeTypeEnum, ImageTypeNameEnum, ItemTypeEnum, ProductComplianceTechnicalHeadingTypeEnum } from '@inter-ikea-kompis/enums';
import IdGenerator from '../generator/IdGenerator.js';
const ITEM_NO_NUMBER_OF_CHARACTERS = 3;
const ENERGY_CLASS_REGEXP = /^(A\+{0,3}|B|C|D|E)/;
const BUNDLE_ID_TYPE_PREFIX = 'BUN-';
/**
 * A product utility that can be used for formatting article numbers.
 */
export default class ProductUtility {
    /**
     * Formats a product article number to be more readable for the end user. The string "12535352" will for example be formatted to "125.353.52".
     *
     * @param itemNo Product item number.
     * @returns An item number including dots.
     */
    static formatItemNo(itemNo) {
        const numberOfDots = Math.ceil(itemNo.length / ITEM_NO_NUMBER_OF_CHARACTERS);
        const parts = Array(numberOfDots)
            .fill(null)
            .map((_, index) => itemNo.slice(index * ITEM_NO_NUMBER_OF_CHARACTERS, index * ITEM_NO_NUMBER_OF_CHARACTERS + ITEM_NO_NUMBER_OF_CHARACTERS));
        return parts.join('.');
    }
    /**
     * Returns product information page link.
     *
     * @param product Product.
     * @param localizedInformationOrSettings Either settings retrieved from settings service or localized information fetched from localization service.
     * @returns Product information page link.
     */
    static getProductInformationPageLink(product, localizedInformationOrSettings) {
        const productInformationPageLinkV2 = 'kompis' in localizedInformationOrSettings
            ? localizedInformationOrSettings.kompis.urls.productInformationPageLinkV2
            : localizedInformationOrSettings.urls.productInformationPageLinkV2;
        if (!product.content || !productInformationPageLinkV2) {
            return null;
        }
        const itemNo = product.content.itemNoLocal || product.content.itemNoGlobal;
        if (!itemNo) {
            return null;
        }
        if (this.isProductSPR(product)) {
            return productInformationPageLinkV2
                .replace('{productItemNo}', itemNo)
                .replace('{lowerCaseSprPrefix}', 's')
                .replace('{upperCaseSprPrefix}', 'S');
        }
        return productInformationPageLinkV2
            .replace('{productItemNo}', itemNo)
            .replace('{lowerCaseSprPrefix}', '')
            .replace('{lowerCaseSprPrefix}', '');
    }
    /**
     * Returns main image.
     *
     * @param product Product.
     * @returns Product images.
     */
    static getMainImages(product) {
        return product.content && product.content.image
            ? product.content.image.filter((image) => image.typeName === ImageTypeNameEnum.mainProductPicture)
            : [];
    }
    /**
     * Checks whether itemType is of type SPR or not.
     *
     * @param product IProduct.
     * @returns Boolean.
     */
    static isProductSPR(product) {
        return !this.isBundleProduct(product) && product.content?.itemType === ItemTypeEnum.spr;
    }
    /**
     * Checks whether the product is a bundle product. Can be used to operate on a
     * bundle with type safety.
     *
     * BundleProduct: A custom built product that does not exist in the eyes of
     * PIA but is presented like an SPR with child articles to the user.
     *
     * @param product IProduct.
     * @returns Product is IBundleProduct, boolean.
     */
    static isBundleProduct(product) {
        return product.itemId.startsWith(BUNDLE_ID_TYPE_PREFIX);
    }
    /**
     * Checks whether the shopping product is a bundle shopping product.
     *
     * BundleProduct: A custom built product that has no item number, but is like an SPR, which contains child articles.
     *
     * @param shoppingProduct IProduct.
     * @returns Product is IBundleProduct, boolean.
     */
    static isBundleShoppingProduct(shoppingProduct) {
        return this.isBundleProduct(shoppingProduct.product);
    }
    /**
     * Returns images based on what enum is passed.
     *
     * @param product Product - the product to fetch the image from..
     * @param imageType ImageTypeNameEnum - what type of image to fetch.
     * @returns Product images.
     */
    static getProductImages(product, imageType) {
        const images = product.content && product.content.image
            ? product.content.image.filter((image) => image.typeName === imageType)
            : [];
        return images;
    }
    /**
     * Returns energy class.
     *
     * @param product Product.
     * @returns Energy class.
     */
    static getEnergyClass(product) {
        if (!product.content?.technicalInformation?.length) {
            return null;
        }
        for (const item of product.content.technicalInformation) {
            if (typeof item.valueMetric === 'string') {
                const match = item.valueMetric.match(ENERGY_CLASS_REGEXP);
                if (match) {
                    return match[1];
                }
            }
        }
        return null;
    }
    /**
     * Returns the link to EPREL for the product. EPREL stands for "European Product Registry for Energy Labelling".
     *
     * @param product ...
     */
    static getProductInformationSheetLink(product) {
        if (product?.content?.complianceTechnical?.length) {
            const productInformationSheetInformation = product.content.complianceTechnical.find((complianceTechnicalItem) => complianceTechnicalItem.headingType === ProductComplianceTechnicalHeadingTypeEnum.rescaled);
            if (productInformationSheetInformation) {
                return {
                    text: productInformationSheetInformation.headingText,
                    href: productInformationSheetInformation.text
                };
            }
        }
        return null;
    }
    /**
     * Returns energy compliance type (EU/US).
     *
     * @param product ...
     */
    static getProductComplianceType(product) {
        if (product?.content?.complianceTechnical?.length) {
            const complianceType = product.content.complianceTechnical.find((complianceTechnicalItem) => complianceTechnicalItem.headingType ===
                ProductComplianceTechnicalHeadingTypeEnum.energyEfficiencyClass ||
                complianceTechnicalItem.headingType ===
                    ProductComplianceTechnicalHeadingTypeEnum.hyperlink ||
                complianceTechnicalItem.headingType ===
                    ProductComplianceTechnicalHeadingTypeEnum.unifiedWaterLabel);
            if (complianceType) {
                return complianceType.headingType;
            }
        }
        return null;
    }
    /**
     * Returns IShoppingItem[] from a IShoppingProduct[].
     *
     * @param shoppingProducts ...
     */
    static getShoppingItems(shoppingProducts) {
        const shoppingItems = [];
        for (const shoppingProduct of shoppingProducts) {
            if (shoppingProduct.product.content?.ruItemNo &&
                shoppingProduct.product.content?.ruItemType) {
                shoppingItems.push({
                    id: shoppingProduct.product.content.ruItemNo,
                    type: shoppingProduct.product.content.ruItemType,
                    quantity: shoppingProduct.quantity
                });
            }
        }
        return shoppingItems;
    }
    /**
     * Returns IShoppingProduct[] for child products of the main product.
     *
     * @param options ...
     * @param options.product The "main" product (with child products/items).
     * @param options.includedProducts Included products (those not being a main product child will be ignored).
     * @param options.ignoreInvalidProducts Whether or not invalid products in includedProducts should be ignored, i.e. Not be returned as shopping product (false is default).
     */
    static getIncludedShoppingProducts(options) {
        const { product, includedProducts, ignoreInvalidProducts = false } = options;
        const shoppingProducts = [];
        for (const child of product.content?.child ?? []) {
            const itemNo = child.itemId.split('-').reverse()[0];
            const includedProduct = includedProducts.find((p) => itemNo === p.content?.itemNoLocal || itemNo === p.content?.itemNoGlobal);
            if (includedProduct && !(ignoreInvalidProducts && !includedProduct.valid)) {
                shoppingProducts.push({
                    product: includedProduct,
                    quantity: child.quantity
                });
            }
        }
        return shoppingProducts;
    }
    /**
     * Returns true if product price should be calculated from the child products of the product.
     *
     * @param product The product.
     * @returns True if product have no price information and have child products, otherwise false.
     */
    static shouldCalculatePriceFromChildProducts(product) {
        const hasPriceInformation = !this.isBundleProduct(product) && product.content?.priceInformation;
        const hasChildren = !!product.content?.child;
        return !hasPriceInformation && hasChildren;
    }
    /**
     * Creates a bundle product and adds additional information such as auto
     * generated itemId and sets `valid` to `true`.
     *
     * @param productData Product data to append to the created product.
     */
    static createBundleProduct(productData) {
        const itemId = this.generateBundleId();
        return {
            ...productData,
            itemId,
            valid: true
        };
    }
    /**
     * Creates an array of IProductImage objects from a base64 image URL. The
     * array will contain multiple images with different sizes to fit all
     * components, even though the same URL is used for all sizes. For this reason
     * it's important that the image passed in is a base64 URL.
     *
     * @param options Options.
     * @param options.base64Url The base64 URL of the image (`data:image/png;base64,...`).
     * @param options.typeName The type name of the image. If not provided, it will default to `mainProductPicture`.
     */
    static createBase64ProductImages(options) {
        const sizes = Object.values(ImageSizeTypeEnum);
        return sizes.map((size) => ({
            size,
            url: options.base64Url,
            typeName: options.typeName ?? ImageTypeNameEnum.mainProductPicture,
            sortNo: 0
        }));
    }
    /**
     * Generates an ID for a bundle product that follows the PIA pattern (XXX-00000000).
     */
    static generateBundleId() {
        const idGenerator = this.getIdGenerator();
        const idNumber = idGenerator.generateNumericId().toString().padStart(8, '0');
        return BUNDLE_ID_TYPE_PREFIX + idNumber;
    }
    /**
     * Gets a unique IdGenerator for the ProductUtility class.
     */
    static getIdGenerator() {
        if (!this.idGenerator) {
            this.idGenerator = new IdGenerator();
        }
        return this.idGenerator;
    }
}
//# sourceMappingURL=ProductUtility.js.map